import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  List,
  ListItemButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { PlaylistAddCheckOutlined, Refresh as RefreshIcon, SearchOutlined } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";

import CustomSnackBar from "../../Components/CustomSnackBar";
import MainText from "../../Components/MainText";
import ActiveGeneralInventoryAlert from "./components/ActiveGeneralInventoryAlert";
import CountGroupSetup, { serializeCountGroupRoles } from "./components/CountGroupSetup";
import ProductFormatSummary from "./components/ProductFormatSummary";
import StockCountPage from "./StockCountPage";
import { hasAnySelectivePermission, hasPermission } from "../../config/permissionUtils";
import { useAuth } from "../../context/AuthContext";
import { inventoryService, productsService } from "../../services";
import useStockCount from "../../hooks/useStockCount";

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatBaseQty = (value) =>
  new Intl.NumberFormat("es-GT", { maximumFractionDigits: 3 }).format(toNumber(value));

const getProductStockQtyBase = (product) => {
  const presentationQtys = toArray(product?.presentations).map((presentation) =>
    toNumber(presentation?.stock_qty_base)
  );
  if (presentationQtys.length > 0) {
    return Math.max(...presentationQtys);
  }
  return product?.stock_summary?.has_stock ? 1 : 0;
};

const getStatusMeta = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "DRAFT") return { label: "En progreso", color: "warning" };
  if (normalized === "PENDING_REVIEW") return { label: "Pendiente de revision", color: "info" };
  if (normalized === "REVIEWING") return { label: "En revision", color: "info" };
  if (normalized === "APPLIED") return { label: "Aplicado", color: "success" };
  if (normalized === "CANCELED") return { label: "Cancelado", color: "default" };
  if (normalized === "CLOSED") return { label: "Cerrado", color: "info" };
  return { label: normalized || "-", color: "default" };
};

function useDebouncedValue(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timeout = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timeout);
  }, [delay, value]);

  return debounced;
}

function StockCountSelectivePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentBranch, isSuperuser, effectivePerms = [] } = useAuth();
  const branchId = currentBranch?.id || null;
  const canViewSelective = hasPermission(effectivePerms, "inventory_selective_view", isSuperuser);
  const canStartSelective = hasPermission(effectivePerms, "inventory_selective_start", isSuperuser);
  const canCountSelective = hasPermission(effectivePerms, "inventory_selective_count", isSuperuser);
  const canAdjustSelective = hasPermission(effectivePerms, "inventory_selective_adjust", isSuperuser);
  const canCancelSelective = hasPermission(effectivePerms, "inventory_selective_cancel", isSuperuser);
  const canAccessSelective = hasAnySelectivePermission(effectivePerms, isSuperuser);
  const isNewRoute = location.pathname.endsWith("/new");
  const canAccessCapture = canViewSelective || canCountSelective || canAdjustSelective;
  const hasCountIdParam = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = Number(params.get("count_id"));
    return Number.isInteger(raw) && raw > 0;
  }, [location.search]);
  const canAccessNewRoute = canStartSelective || (hasCountIdParam && canAccessCapture);

  const stockCount = useStockCount("SELECTIVE", branchId);
  const {
    countId,
    countStatus,
    countAppliedAt,
    error: stockCountError,
    filterMode,
    filteredItems,
    isReadOnly,
    items,
    groups,
    loading: stockCountLoading,
    refreshCount,
    searchQuery,
    setFilterMode,
    setSearchQuery,
    addToItem,
    addEntry,
    updateEntry,
    voidEntry,
    resetItem,
    deleteProduct,
    submitCount,
    settingsSnapshot,
    crosses,
  } = stockCount;
  const hasActiveCount = Boolean(countId);

  const [counts, setCounts] = useState([]);
  const [countsLoading, setCountsLoading] = useState(false);
  const [productQuery, setProductQuery] = useState("");
  const [keepProductSearchOpen, setKeepProductSearchOpen] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [countSettings, setCountSettings] = useState(null);
  const [groupCountDraft, setGroupCountDraft] = useState(1);
  const [groupRolesDraft, setGroupRolesDraft] = useState([]);
  const [countListFilter, setCountListFilter] = useState("ALL");
  const [countListSearch, setCountListSearch] = useState("");
  const [creatingCount, setCreatingCount] = useState(false);
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [cancelDialog, setCancelDialog] = useState({ open: false, count: null });
  const [removeSelectedProductDialog, setRemoveSelectedProductDialog] = useState({ open: false, product: null });
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");
  const restoredDraftKeyRef = useRef(null);

  const debouncedQuery = useDebouncedValue(productQuery, 300);
  const draftStorageKey = useMemo(
    () => (branchId ? `stock-count-selective-draft:${branchId}` : null),
    [branchId]
  );
  const activeGroupStorageKey = useMemo(
    () => (branchId && countId ? `stock-count-selective-active-group:${branchId}:${countId}` : null),
    [branchId, countId]
  );

  const showSnack = (message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  };

  const loadCounts = useCallback(async () => {
    if (!branchId || !canAccessSelective) {
      setCounts([]);
      return;
    }

    setCountsLoading(true);
    try {
      const data = await inventoryService.listStockCounts({ branch_id: branchId, count_type: "SELECTIVE" });
      setCounts(toArray(data));
    } catch (error) {
      setCounts([]);
      showSnack(error?.response?.data?.detail || "No se pudo cargar el listado de conteos.", "error");
    } finally {
      setCountsLoading(false);
    }
  }, [branchId, canAccessSelective]);

  const loadCountSettings = useCallback(async () => {
    if (!branchId || !canAccessSelective) {
      setCountSettings(null);
      return;
    }
    try {
      const data = await inventoryService.getCountSettings({ branch_id: branchId });
      setCountSettings(data || null);
    } catch {
      setCountSettings(null);
    }
  }, [branchId, canAccessSelective]);

  const handleRefresh = useCallback(async () => {
    await Promise.all([
      loadCounts(),
      loadCountSettings(),
      countId ? refreshCount().catch(() => null) : Promise.resolve(null),
    ]);
  }, [countId, loadCountSettings, loadCounts, refreshCount]);

  useEffect(() => {
    loadCounts();
    loadCountSettings();
  }, [loadCountSettings, loadCounts]);

  useEffect(() => {
    if (!draftStorageKey || hasActiveCount) return;
    if (restoredDraftKeyRef.current === draftStorageKey) return;
    restoredDraftKeyRef.current = draftStorageKey;

    try {
      const rawDraft = window.sessionStorage.getItem(draftStorageKey);
      if (!rawDraft) return;
      const parsedDraft = JSON.parse(rawDraft);
      setSelectedProducts(toArray(parsedDraft?.selectedProducts));
      setProductQuery(typeof parsedDraft?.productQuery === "string" ? parsedDraft.productQuery : "");
      setGroupCountDraft(Number(parsedDraft?.groupCountDraft) || 1);
      setGroupRolesDraft(toArray(parsedDraft?.groupRolesDraft));
    } catch {
      setSelectedProducts([]);
      setProductQuery("");
      setGroupCountDraft(1);
      setGroupRolesDraft([]);
    }
  }, [draftStorageKey, hasActiveCount]);

  useEffect(() => {
    if (!draftStorageKey || hasActiveCount) return;

    if (
      selectedProducts.length === 0 &&
      !String(productQuery || "").trim() &&
      groupRolesDraft.length === 0
    ) {
      window.sessionStorage.removeItem(draftStorageKey);
      return;
    }

    window.sessionStorage.setItem(
      draftStorageKey,
      JSON.stringify({
        selectedProducts,
        productQuery,
        groupCountDraft,
        groupRolesDraft,
      })
    );
  }, [draftStorageKey, groupCountDraft, groupRolesDraft, hasActiveCount, productQuery, selectedProducts]);

  useEffect(() => {
    if (!activeGroupStorageKey) {
      setActiveGroupId(null);
      return;
    }
    const storedValue = window.sessionStorage.getItem(activeGroupStorageKey);
    setActiveGroupId(storedValue || null);
  }, [activeGroupStorageKey]);

  const updateActiveGroupId = useCallback(
    (nextGroupId) => {
      const normalized = nextGroupId ? String(nextGroupId) : "";
      setActiveGroupId(normalized || null);
      if (!activeGroupStorageKey) return;
      if (normalized) {
        window.sessionStorage.setItem(activeGroupStorageKey, normalized);
      } else {
        window.sessionStorage.removeItem(activeGroupStorageKey);
      }
    },
    [activeGroupStorageKey]
  );

  useEffect(() => {
    if (!countId || stockCountLoading || stockCountError || items.length > 0) {
      return;
    }
    refreshCount().catch(() => {});
  }, [countId, stockCountError, items.length, stockCountLoading, refreshCount]);

  const activeCount = useMemo(
    () =>
      counts.find((row) =>
        ["DRAFT", "PENDING_REVIEW", "REVIEWING"].includes(String(row?.status || "").toUpperCase())
      ) || null,
    [counts]
  );

  const selectiveCounts = useMemo(
    () => counts.filter((row) => String(row?.count_type || "").toUpperCase() === "SELECTIVE"),
    [counts]
  );

  const activeSelectiveCounts = useMemo(
    () =>
      selectiveCounts.filter((row) =>
        ["DRAFT", "PENDING_REVIEW", "REVIEWING"].includes(String(row?.status || "").toUpperCase())
      ),
    [selectiveCounts]
  );
  const activeGeneralInventory = countSettings?.active_general_inventory || null;
  const activeGeneralInventoryId = activeGeneralInventory?.id || null;
  const allowParallelCounts = Boolean(countSettings?.selective_allow_parallel_counts);
  const blocksNewSelective = Boolean(isNewRoute && !allowParallelCounts && activeSelectiveCounts.length > 0);
  const blockedActiveProductIds = useMemo(() => {
    const ids = new Set();
    activeSelectiveCounts.forEach((count) => {
      toArray(count?.product_ids).forEach((productId) => {
        if (productId) ids.add(String(productId));
      });
    });
    return ids;
  }, [activeSelectiveCounts]);

  const countGroupsEnabled = Boolean(countSettings?.selective_enable_count_groups);
  const allowSalesDuringCount = Boolean(countSettings?.selective_allow_sales_during_count);
  const allowZeroStockProducts = Boolean(countSettings?.selective_allow_zero_stock_products);
  const pendingCashierSelectedProducts = useMemo(
    () => selectedProducts.filter((product) => Boolean(product?.hasPendingCashierSales)),
    [selectedProducts]
  );
  const hasPendingCashierConflict = pendingCashierSelectedProducts.length > 0 && !allowSalesDuringCount;
  const hasBlockedZeroStockSelectedProduct = useMemo(
    () => selectedProducts.some((product) => Boolean(product?.hasZeroStock) && !allowZeroStockProducts),
    [allowZeroStockProducts, selectedProducts]
  );

  useEffect(() => {
    if (!countGroupsEnabled || !activeGroupId) return;
    const exists = toArray(groups).some((group) => String(group?.id) === String(activeGroupId));
    if (!exists && toArray(groups).length > 0) {
      updateActiveGroupId(null);
    }
  }, [activeGroupId, countGroupsEnabled, groups, updateActiveGroupId]);

  const createCountGroup = useCallback(
    async (payload) => {
      if (!branchId || !countId) return null;
      const group = await inventoryService.createStockCountGroup(countId, {
        branch_id: branchId,
        ...payload,
      });
      await refreshCount();
      return group;
    },
    [branchId, countId, refreshCount]
  );

  const joinCountGroup = useCallback(
    async (groupId, payload = {}) => {
      if (!branchId || !countId || !groupId) return null;
      const group = await inventoryService.joinStockCountGroup(countId, groupId, {
        branch_id: branchId,
        ...payload,
      });
      await refreshCount();
      return group;
    },
    [branchId, countId, refreshCount]
  );

  const addCountGroupMember = useCallback(
    async (groupId, payload = {}) => {
      if (!branchId || !countId || !groupId) return null;
      const group = await inventoryService.addStockCountGroupMember(countId, groupId, {
        branch_id: branchId,
        ...payload,
      });
      await refreshCount();
      return group;
    },
    [branchId, countId, refreshCount]
  );

  const visibleSelectiveCounts = useMemo(() => {
    const normalizedSearch = String(countListSearch || "").trim().toLowerCase();
    return selectiveCounts.filter((count) => {
      const status = String(count?.status || "").toUpperCase();
      if (countListFilter === "DRAFT" && status !== "DRAFT") return false;
      if (countListFilter === "PENDING_REVIEW" && status !== "PENDING_REVIEW") return false;
      if (countListFilter === "REVIEWING" && status !== "REVIEWING") return false;
      if (countListFilter === "APPLIED" && status !== "APPLIED") return false;
      if (countListFilter === "CANCELED" && status !== "CANCELED") return false;
      if (!normalizedSearch) return true;
      const haystack = [
        count?.id,
        count?.comment,
        count?.products_summary,
        count?.created_by_name,
        count?.created_at,
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [countListFilter, countListSearch, selectiveCounts]);

  const groupedSelectiveCounts = useMemo(() => {
    const groups = new Map();
    visibleSelectiveCounts.forEach((count) => {
      const date = new Date(count?.created_at);
      const key = Number.isNaN(date.getTime())
        ? "Sin fecha"
        : date.toLocaleDateString("es-GT", { month: "long", year: "numeric" });
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(count);
    });
    return Array.from(groups.entries()).map(([label, rows]) => ({ label, rows }));
  }, [visibleSelectiveCounts]);

  const selectedProductIds = useMemo(
    () => new Set(selectedProducts.map((product) => String(product.id))),
    [selectedProducts]
  );

  const visibleSearchResults = useMemo(
    () =>
      searchResults
        .filter((product) => !selectedProductIds.has(String(product.id)))
        .map((product) => ({
          ...product,
          blockedInActiveSelective: blockedActiveProductIds.has(String(product?.id)),
          hasZeroStock: getProductStockQtyBase(product) <= 0.0001,
          blockedByZeroStock: !allowZeroStockProducts && getProductStockQtyBase(product) <= 0.0001,
        })),
    [allowZeroStockProducts, blockedActiveProductIds, searchResults, selectedProductIds]
  );

  useEffect(() => {
    if (!branchId || debouncedQuery.trim().length < 2 || hasActiveCount || blocksNewSelective || activeGeneralInventoryId) {
      setSearchResults([]);
      return;
    }

    let cancelled = false;
    (async () => {
      setSearchLoading(true);
      try {
        const data = await productsService.list({
          sales_search: "true",
          branch_id: branchId,
          search: debouncedQuery.trim(),
          active: "true",
          limit: 20,
        });
        if (!cancelled) {
          setSearchResults(toArray(data));
        }
      } catch {
        if (!cancelled) {
          setSearchResults([]);
        }
      } finally {
        if (!cancelled) {
          setSearchLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeGeneralInventoryId, blocksNewSelective, branchId, debouncedQuery, hasActiveCount]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [debouncedQuery, visibleSearchResults.length]);

  const addProduct = async (product) => {
    if (!branchId || !product?.id) return;
    if (blocksNewSelective) {
      showSnack("Hay un selectivo en curso. No puedes iniciar otro mientras esta opcion este deshabilitada.", "warning");
      return;
    }
    if (activeGeneralInventoryId) {
      showSnack("Hay un inventario general activo. No puedes iniciar un conteo selectivo mientras siga en progreso.", "warning");
      return;
    }
    if (blockedActiveProductIds.has(String(product.id))) {
      showSnack("Este producto ya esta siendo contado en otro selectivo activo.", "warning");
      return;
    }
    if (!allowZeroStockProducts && (product?.blockedByZeroStock || getProductStockQtyBase(product) <= 0.0001)) {
      showSnack("El producto seleccionado no tiene existencia en esta sucursal.", "warning");
      return;
    }
    if (selectedProductIds.has(String(product.id))) {
      showSnack("Ese producto ya esta agregado al conteo selectivo.", "info");
      return;
    }

    try {
      const stockRows = await inventoryService.listStocks({
        branch_id: branchId,
        product_id: product.id,
      });
      const stockRowsArray = toArray(stockRows);
      const stockPresentationIds = [
        ...new Set(
          stockRowsArray
            .map((row) => Number(row?.product_presentation_id))
            .filter((value) => Number.isInteger(value) && value > 0)
        ),
      ];
      const productStockQtyBase = stockRowsArray.reduce(
        (maxQty, row) => Math.max(maxQty, toNumber(row?.qty_base)),
        0
      );
      const productHasStock = productStockQtyBase > 0.0001;
      if (!productHasStock && !allowZeroStockProducts) {
        showSnack("El producto seleccionado no tiene existencia en esta sucursal.", "warning");
        return;
      }
      const activePresentationIds = [
        ...new Set(
          toArray(product?.presentations)
            .map((presentation) => Number(presentation?.id))
            .filter((value) => Number.isInteger(value) && value > 0)
        ),
      ];
      const effectivePresentationIds = stockPresentationIds.length > 0 ? stockPresentationIds : activePresentationIds;
      if (effectivePresentationIds.length === 0) {
        showSnack("El producto seleccionado no tiene presentaciones con stock asociado en esta sucursal.", "warning");
        return;
      }
      const pendingCashierQtyBase = stockRowsArray.reduce(
        (maxQty, row) => Math.max(maxQty, toNumber(row?.qty_reserved_hard)),
        0
      );
      const hasPendingCashierSales = pendingCashierQtyBase > 0.0001;
      if (hasPendingCashierSales && !allowSalesDuringCount) {
        showSnack(
          "Este producto tiene ventas pendientes de cobro en Caja. Finaliza o cancela esas ventas antes de iniciar el selectivo.",
          "warning"
        );
        return;
      }

      const matchedPresentations = toArray(product?.presentations).filter((presentation) =>
        effectivePresentationIds.includes(Number(presentation?.id))
      );
      setSelectedProducts((prev) => [
        ...prev,
        {
          id: product.id,
          code: product.code || "",
          description: product.description || "Producto",
          max_presentation_name: product.max_presentation_name,
          max_presentation_qty_base: product.max_presentation_qty_base,
          presentations_summary: product.presentations_summary,
          presentations: matchedPresentations,
          presentationIds: effectivePresentationIds,
          hasZeroStock: !productHasStock,
          stockQtyBase: productStockQtyBase,
          hasPendingCashierSales,
          pendingCashierQtyBase,
        },
      ]);
      setSearchResults((prev) => prev.filter((row) => String(row?.id) !== String(product.id)));
      if (!keepProductSearchOpen) {
        setProductQuery("");
      }
    } catch {
      showSnack("No se pudo preparar el producto para el conteo selectivo.", "error");
    }
  };

  const allPresentationIds = useMemo(
    () => [
      ...new Set(
        selectedProducts.flatMap((product) =>
          toArray(product?.presentationIds).filter((value) => Number.isInteger(value) && value > 0)
        )
      ),
    ],
    [selectedProducts]
  );
  const hasBlockedSelectedProduct = useMemo(
    () => selectedProducts.some((product) => blockedActiveProductIds.has(String(product?.id))),
    [blockedActiveProductIds, selectedProducts]
  );

  const sortedSelectedProducts = useMemo(() => [...selectedProducts], [selectedProducts]);

  const openCount = useCallback(
    (count) => {
      if (!count?.id) return;
      const status = String(count?.status || "").toUpperCase();
      if (["PENDING_REVIEW", "REVIEWING", "APPLIED", "CANCELED", "CLOSED"].includes(status)) {
        navigate(`/inventory/counts/${count.id}/review`);
        return;
      }
      const targetPath =
        String(count?.count_type || "").toUpperCase() === "GENERAL"
          ? `/inventory/counts/general/capture?count_id=${count.id}`
          : `/inventory/counts/selective/new?count_id=${count.id}`;
      navigate(targetPath);
    },
    [navigate]
  );

  const handleStartCount = useCallback(async () => {
    if (!branchId || allPresentationIds.length === 0) return;
    if (blocksNewSelective) {
      showSnack("Hay un selectivo en curso. No puedes iniciar otro mientras esta opcion este deshabilitada.", "warning");
      return;
    }
    if (activeGeneralInventoryId) {
      showSnack("Hay un inventario general activo. No puedes iniciar un conteo selectivo mientras siga en progreso.", "warning");
      return;
    }
    if (countGroupsEnabled && groupRolesDraft.length === 0) {
      showSnack("Debes definir al menos un grupo de conteo.", "warning");
      return;
    }
    if (hasBlockedSelectedProduct) {
      showSnack("Uno o mas productos ya estan siendo contados en otro selectivo activo.", "warning");
      return;
    }
    if (hasPendingCashierConflict) {
      showSnack("Hay productos con ventas pendientes de cobro en Caja. Finaliza o cancela esas ventas antes de iniciar.", "warning");
      return;
    }
    if (hasBlockedZeroStockSelectedProduct) {
      showSnack("Hay productos sin existencia seleccionados. Quitar esos productos o habilita el ajuste correspondiente.", "warning");
      return;
    }
    setCreatingCount(true);
    try {
      if (!canStartSelective) {
        showSnack("No autorizado para iniciar conteos selectivos.", "warning");
        return;
      }
      await stockCount.startCount({
        count_type: "SELECTIVE",
        presentation_ids: allPresentationIds,
        group_count: countGroupsEnabled ? groupRolesDraft.length : undefined,
        group_roles: countGroupsEnabled ? serializeCountGroupRoles(groupRolesDraft) : undefined,
      });
      if (draftStorageKey) {
        window.sessionStorage.removeItem(draftStorageKey);
      }
      setSelectedProducts([]);
      setProductQuery("");
      setGroupCountDraft(1);
      setGroupRolesDraft([]);
      setSearchResults([]);
      showSnack("Conteo selectivo iniciado.", "success");
      await loadCounts();
      await loadCountSettings();
    } catch (error) {
      const pendingRows = toArray(error?.response?.data?.pending_cashier_sales);
      const pendingDetail = pendingRows.length
        ? ` ${pendingRows
            .slice(0, 3)
            .map(
              (row) =>
                `${row?.product_code || "Sin codigo"}: ${formatBaseQty(row?.pending_cashier_sales_qty_base)} base`
            )
            .join("; ")}${pendingRows.length > 3 ? "..." : ""}`
        : "";
      showSnack(
        `${error?.response?.data?.detail || "No se pudo iniciar el conteo selectivo."}${pendingDetail}`,
        "error"
      );
      await loadCounts();
      await loadCountSettings();
    } finally {
      setCreatingCount(false);
    }
  }, [activeGeneralInventoryId, allPresentationIds, blocksNewSelective, branchId, canStartSelective, countGroupsEnabled, draftStorageKey, groupRolesDraft, hasBlockedSelectedProduct, hasBlockedZeroStockSelectedProduct, hasPendingCashierConflict, loadCountSettings, loadCounts, stockCount]);

  if ((hasActiveCount && !canAccessCapture) || (!hasActiveCount && !isNewRoute && !canAccessSelective) || (!hasActiveCount && isNewRoute && !canAccessNewRoute)) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Sin permiso
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No tienes permisos para realizar conteos fisicos.
        </Typography>
      </Box>
    );
  }

  if (hasActiveCount) {
    const editableInReview =
      canAdjustSelective &&
      ["PENDING_REVIEW", "REVIEWING"].includes(String(countStatus || "").toUpperCase());
    const captureReadOnly = (isReadOnly && !editableInReview) || (!canCountSelective && !editableInReview);
    return (
      <>
        <StockCountPage
          title="Conteo selectivo"
          subtitle="Conteo por productos elegidos antes de iniciar"
          countId={countId}
          countStatus={countStatus}
          countAppliedAt={countAppliedAt}
          items={items}
          loading={stockCountLoading}
          error={stockCountError}
          filterMode={filterMode}
          setFilterMode={setFilterMode}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          filteredItems={filteredItems}
          addToItem={addToItem}
          addEntry={addEntry}
          updateEntry={updateEntry}
          voidEntry={voidEntry}
          resetItem={resetItem}
          deleteProduct={deleteProduct}
          submitCount={submitCount}
          isReadOnly={captureReadOnly}
          settingsSnapshot={settingsSnapshot}
          groups={groups}
          crosses={crosses}
          activeGroupId={activeGroupId}
          onActiveGroupChange={updateActiveGroupId}
          onCreateGroup={createCountGroup}
          onJoinGroup={joinCountGroup}
          onAddGroupMember={addCountGroupMember}
          isReviewCorrectionMode={editableInReview}
          reviewReturnPath={`/inventory/counts/${countId}/review`}
          onSubmitted={async (updatedCount) => {
            const targetId = updatedCount?.id || countId;
            stockCount.clearCount();
            if (activeGroupStorageKey) {
              window.sessionStorage.removeItem(activeGroupStorageKey);
            }
            navigate(`/inventory/counts/${targetId}/review`, { replace: true });
          }}
        />
        <CustomSnackBar open={snackOpen} setOpen={setSnackOpen} message={snackMsg} severity={snackSev} />
      </>
    );
  }

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.2}>
        <MainText
          title={isNewRoute ? "Nuevo conteo selectivo" : "Selectivos"}
          subtitle={isNewRoute ? "Busca productos y prepara la captura" : "Listado historico y seguimiento de selectivos"}
          icon={PlaylistAddCheckOutlined}
          width="50%"
        />
        <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={countsLoading}>
            Refrescar
          </Button>
          {!isNewRoute && canStartSelective ? (
            <Button variant="contained" onClick={() => navigate("/inventory/counts/selective/new")}>
              Nuevo selectivo
            </Button>
          ) : null}
        </Stack>
      </Stack>

      {stockCountError ? <Alert severity="error">{stockCountError}</Alert> : null}

      <ActiveGeneralInventoryAlert activeInventory={activeGeneralInventory} />

      {activeCount && String(activeCount?.count_type || "").toUpperCase() === "SELECTIVE" && !allowParallelCounts ? (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={() => openCount(activeCount)}>
              Retomar
            </Button>
          }
        >
          Ya existe un conteo selectivo activo para esta sucursal.
        </Alert>
      ) : null}

      {!isNewRoute ? (
      <Paper variant="outlined" sx={{ p: 1.4 }}>
        <Stack spacing={1}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} spacing={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              Conteos selectivos
            </Typography>
            <Chip size="small" label={`${selectiveCounts.length} registros`} variant="outlined" />
          </Stack>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
            <Stack direction="row" spacing={0.6} useFlexGap flexWrap="wrap">
              {[
                { key: "ALL", label: "Todos" },
                { key: "DRAFT", label: "En captura" },
                { key: "PENDING_REVIEW", label: "Pendientes de revision" },
                { key: "REVIEWING", label: "En revision" },
                { key: "APPLIED", label: "Aplicados" },
                { key: "CANCELED", label: "Cancelados" },
              ].map((option) => (
                <Chip
                  key={option.key}
                  clickable
                  label={option.label}
                  color={countListFilter === option.key ? "primary" : "default"}
                  variant={countListFilter === option.key ? "filled" : "outlined"}
                  onClick={() => setCountListFilter(option.key)}
                />
              ))}
            </Stack>
            <TextField
              size="small"
              placeholder="Buscar por ID, producto, usuario o fecha"
              value={countListSearch}
              onChange={(event) => setCountListSearch(event.target.value)}
              sx={{ minWidth: { xs: "100%", md: 320 }, ml: { md: "auto" } }}
            />
          </Stack>

          {countsLoading ? (
            <Typography variant="body2" color="text.secondary">
              Cargando conteos...
            </Typography>
          ) : visibleSelectiveCounts.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No hay conteos selectivos para los filtros actuales.
            </Typography>
          ) : (
            <Stack spacing={1.2}>
              {groupedSelectiveCounts.map((group) => (
                <Stack key={group.label} spacing={0.8}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, textTransform: "capitalize" }}>
                    {group.label}
                  </Typography>
                  {group.rows.map((count) => {
                    const statusMeta = getStatusMeta(count?.status);
                    const statusValue = String(count?.status || "").toUpperCase();
                    const canCancelCount = ["DRAFT", "PENDING_REVIEW", "REVIEWING"].includes(statusValue);
                    return (
                      <Paper key={count.id} variant="outlined" sx={{ p: 1.1 }}>
                        <Stack direction={{ xs: "column", md: "row" }} spacing={1} justifyContent="space-between">
                          <Box sx={{ minWidth: 0 }}>
                            <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                Selectivo #{count.id}
                              </Typography>
                              <Chip size="small" color={statusMeta.color} label={statusMeta.label} />
                            </Stack>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.4 }}>
                              {formatDateTime(count?.created_at)}
                              {count?.created_by_name ? ` | ${count.created_by_name}` : ""}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Contados: {count?.items_counted || 0} / {count?.items_total || 0}
                              {count?.comment ? ` | ${count.comment}` : ""}
                            </Typography>
                          </Box>

                          <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Button size="small" variant="outlined" onClick={() => openCount(count)}>
                              {statusValue === "DRAFT" ? "Retomar" : statusValue === "APPLIED" ? "Ver" : "Revisar"}
                            </Button>
                            {canCancelCount && canCancelSelective ? (
                              <Button
                                size="small"
                                color="error"
                                onClick={() => setCancelDialog({ open: true, count })}
                              >
                                Anular
                              </Button>
                            ) : null}
                          </Stack>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      </Paper>
      ) : null}

      {isNewRoute ? (
      <>
      <Alert severity="info" sx={{ maxWidth: 920 }}>
        Agrega uno o varios productos. El conteo se creara con todas las presentaciones de esos productos que tengan stock asociado en esta sucursal.
      </Alert>

      {blocksNewSelective ? (
        <Alert severity="warning" sx={{ maxWidth: 920 }}>
          Hay un selectivo en curso. No puedes iniciar otro mientras esta opcion este deshabilitada.
        </Alert>
      ) : activeGeneralInventoryId ? (
        <Alert severity="error" sx={{ maxWidth: 920 }}>
          No puedes iniciar un conteo selectivo porque la sucursal esta en inventario general.
        </Alert>
      ) : allowParallelCounts && activeSelectiveCounts.length > 0 ? (
        <Alert severity="info" sx={{ maxWidth: 920 }}>
          Hay {activeSelectiveCounts.length} selectivos en curso. No podras seleccionar productos que ya esten siendo contados.
        </Alert>
      ) : (
        <Alert severity="info" sx={{ maxWidth: 920 }}>
          Estos ajustes se bloquean mientras exista un conteo selectivo activo o pendiente de revision.
        </Alert>
      )}

      {pendingCashierSelectedProducts.length > 0 ? (
        <Alert severity={allowSalesDuringCount ? "warning" : "error"} sx={{ maxWidth: 920 }}>
          Hay {pendingCashierSelectedProducts.length} productos con ventas pendientes de cobro en Caja.
          {allowSalesDuringCount
            ? " El selectivo puede iniciar, pero revisa la disponibilidad estimada durante captura y revision."
            : " Finaliza o cancela esas ventas antes de iniciar el selectivo."}
        </Alert>
      ) : null}

      {hasBlockedZeroStockSelectedProduct ? (
        <Alert severity="error" sx={{ maxWidth: 920 }}>
          Hay productos sin existencia seleccionados. Si el ajuste esta deshabilitado, solo puedes iniciar selectivos con stock mayor a 0.
        </Alert>
      ) : null}

      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={keepProductSearchOpen}
            onChange={(event) => setKeepProductSearchOpen(event.target.checked)}
          />
        }
        label="Mantener buscador abierto"
        sx={{ alignSelf: "flex-start" }}
      />

      {countGroupsEnabled ? (
        <Paper variant="outlined" sx={{ p: 1.4, maxWidth: 920 }}>
          <Stack spacing={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              Grupos de conteo
            </Typography>
            <CountGroupSetup
              groupCount={groupCountDraft}
              groups={groupRolesDraft}
              onGroupCountChange={setGroupCountDraft}
              onGroupsChange={setGroupRolesDraft}
              onGenerate={() => {
                const count = Math.min(20, Math.max(1, Number(groupCountDraft) || 1));
                setGroupCountDraft(count);
                setGroupRolesDraft(
                  Array.from({ length: count }, (_, index) =>
                    groupRolesDraft[index] || { digitatorsText: "", countersText: "" }
                  )
                );
              }}
            />
          </Stack>
        </Paper>
      ) : null}

      <Box sx={{ position: "relative" }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Buscar producto para agregar al conteo..."
          disabled={blocksNewSelective || Boolean(activeGeneralInventoryId)}
          value={productQuery}
          onChange={(event) => setProductQuery(event.target.value)}
          onKeyDown={(event) => {
            if (blocksNewSelective) return;
            if (event.key === "ArrowDown" && visibleSearchResults.length > 0) {
              event.preventDefault();
              setHighlightedIndex((prev) => Math.min(prev + 1, visibleSearchResults.length - 1));
            }
            if (event.key === "ArrowUp" && visibleSearchResults.length > 0) {
              event.preventDefault();
              setHighlightedIndex((prev) => Math.max(prev - 1, 0));
            }
            if (event.key === "Enter" && visibleSearchResults[highlightedIndex]) {
              event.preventDefault();
              const highlightedProduct = visibleSearchResults[highlightedIndex];
              if (!highlightedProduct?.blockedInActiveSelective && !highlightedProduct?.blockedByZeroStock) {
                addProduct(highlightedProduct);
              }
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {productQuery.trim().length >= 2 ? (
          <Paper
            variant="outlined"
            sx={{
              position: "absolute",
              zIndex: 3,
              left: 0,
              right: 0,
              mt: 0.6,
              maxHeight: 280,
              overflowY: "auto",
            }}
          >
            {searchLoading ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 1.2 }}>
                Buscando...
              </Typography>
            ) : visibleSearchResults.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 1.2 }}>
                Sin resultados disponibles para agregar.
              </Typography>
            ) : (
              <List disablePadding>
                {visibleSearchResults.map((product, index) => (
                  <ListItemButton
                    key={product.id}
                    selected={index === highlightedIndex}
                    disabled={Boolean(product.blockedInActiveSelective || product.blockedByZeroStock)}
                    onClick={() => addProduct(product)}
                    sx={{ alignItems: "flex-start", py: 1 }}
                  >
                    <Box sx={{ minWidth: 0, width: "100%" }}>
                      <ProductFormatSummary product={product} />
                      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.6 }}>
                        {product.blockedInActiveSelective ? (
                          <Chip size="small" color="warning" label="En otro selectivo" />
                        ) : null}
                        {product.hasZeroStock ? (
                          <Chip
                            size="small"
                            color={product.blockedByZeroStock ? "error" : "default"}
                            variant={product.blockedByZeroStock ? "filled" : "outlined"}
                            label="Sin existencia"
                          />
                        ) : null}
                        {toArray(product?.presentations).map((presentation) => (
                          <Chip
                            key={`search-presentation-${product.id}-${presentation.id}`}
                            size="small"
                            variant="outlined"
                            label={presentation?.product_format?.name || "Presentacion"}
                          />
                        ))}
                      </Stack>
                    </Box>
                  </ListItemButton>
                ))}
              </List>
            )}
          </Paper>
        ) : null}
      </Box>

      <Paper variant="outlined" sx={{ p: 1.4 }}>
        <Stack spacing={1}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Productos seleccionados
          </Typography>

          {selectedProducts.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Todavia no has agregado productos al conteo selectivo.
            </Typography>
          ) : (
            <Stack spacing={0.9}>
              {sortedSelectedProducts.map((product) => (
                <Paper key={product.id} variant="outlined" sx={{ p: 1.1 }}>
                  <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={0.6} alignItems="center">
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <ProductFormatSummary product={product} />
                        </Box>
                        {blockedActiveProductIds.has(String(product.id)) ? (
                          <Chip size="small" color="warning" label="En otro selectivo" />
                        ) : null}
                        {product.hasZeroStock ? (
                          <Chip
                            size="small"
                            color={allowZeroStockProducts ? "default" : "error"}
                            variant={allowZeroStockProducts ? "outlined" : "filled"}
                            label="Sin existencia"
                          />
                        ) : null}
                        {product.hasPendingCashierSales ? (
                          <Chip
                            size="small"
                            color={allowSalesDuringCount ? "warning" : "error"}
                            label={`Pendiente en Caja: ${formatBaseQty(product.pendingCashierQtyBase)} base`}
                          />
                        ) : null}
                      </Stack>
                      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.7 }}>
                        {toArray(product?.presentations).map((presentation) => (
                          <Chip
                            key={`selected-presentation-${product.id}-${presentation.id}`}
                            size="small"
                            label={presentation?.product_format?.name || "Presentacion"}
                          />
                        ))}
                      </Stack>
                    </Box>
                    <Stack direction="row" spacing={0.7}>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => setRemoveSelectedProductDialog({ open: true, product })}
                      >
                        Quitar
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </Stack>
      </Paper>

      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          zIndex: 2,
          bgcolor: "background.paper",
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          py: 1,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          variant="contained"
          disabled={
            !canStartSelective ||
            blocksNewSelective ||
            Boolean(activeGeneralInventoryId) ||
            hasBlockedSelectedProduct ||
            hasBlockedZeroStockSelectedProduct ||
            hasPendingCashierConflict ||
            (countGroupsEnabled && groupRolesDraft.length === 0) ||
            allPresentationIds.length === 0 ||
            creatingCount
          }
          onClick={handleStartCount}
        >
          {creatingCount ? "Iniciando..." : "Iniciar conteo"}
        </Button>
      </Box>
      </>
      ) : null}

      <Dialog
        open={removeSelectedProductDialog.open}
        onClose={() => setRemoveSelectedProductDialog({ open: false, product: null })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Eliminar producto del conteo</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.2}>
            <Typography variant="body2">
              Estas seguro de eliminar este producto del conteo selectivo?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Esta accion eliminara sus cantidades capturadas, sus entradas de conteo y cruces asociados aun no aplicados.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveSelectedProductDialog({ open: false, product: null })}>
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={!removeSelectedProductDialog.product?.id}
            onClick={() => {
              const productId = removeSelectedProductDialog.product?.id;
              setSelectedProducts((prev) => prev.filter((row) => row.id !== productId));
              setRemoveSelectedProductDialog({ open: false, product: null });
            }}
          >
            Eliminar producto
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancelDialog.open}
        onClose={() => {
          if (cancelSubmitting) return;
          setCancelDialog({ open: false, count: null });
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Anular conteo selectivo</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            Se perderan los avances del conteo activo y ya no podras retomarlo. Esta accion no modifica stock.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCancelDialog({ open: false, count: null })}
            disabled={cancelSubmitting}
          >
            Mantener
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={!canCancelSelective || cancelSubmitting || !cancelDialog.count?.id}
            onClick={async () => {
              if (!branchId || !cancelDialog.count?.id) return;
              setCancelSubmitting(true);
              try {
                await inventoryService.cancelStockCount(cancelDialog.count.id, { branch_id: branchId });
                setCancelDialog({ open: false, count: null });
                showSnack("Conteo anulado correctamente.", "success");
                stockCount.clearCount();
                if (draftStorageKey) {
                  window.sessionStorage.removeItem(draftStorageKey);
                }
                await loadCounts();
              } catch (error) {
                showSnack(error?.response?.data?.detail || "No se pudo anular el conteo.", "error");
              } finally {
                setCancelSubmitting(false);
              }
            }}
          >
            {cancelSubmitting ? "Anulando..." : "Si, anular"}
          </Button>
        </DialogActions>
      </Dialog>

      <CustomSnackBar open={snackOpen} setOpen={setSnackOpen} message={snackMsg} severity={snackSev} />
    </Box>
  );
}

export default StockCountSelectivePage;
